import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../store'
import type {
  WhatIfScenario,
  ScenarioTacticSelection,
  TacticInclusionState,
  MECETable,
  ScenarioExplorationState
} from '@models/index'

// State structure for scenarios by conversation
interface ScenariosState {
  byConversation: Record<string, ScenarioExplorationState>
}

const initialState: ScenariosState = {
  byConversation: {}
}

// Helper to create a baseline scenario from MECE table
function createBaselineFromMece(meceTable: MECETable): WhatIfScenario {
  const selections: ScenarioTacticSelection[] = []

  meceTable.pillars.forEach((pillar, pillarIdx) => {
    pillar.tactics.forEach((_, tacticIdx) => {
      selections.push({
        pillar_index: pillarIdx,
        tactic_index: tacticIdx,
        inclusion: 'included' // Baseline includes everything
      })
    })
  })

  return {
    id: `baseline-${Date.now()}`,
    name: 'Baseline (All Tactics)',
    description: 'Current strategy with all tactics included',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    tactic_selections: selections,
    is_baseline: true
  }
}

// Helper to create a fresh scenario with all tactics as 'maybe'
function createFreshScenario(meceTable: MECETable, name: string): WhatIfScenario {
  const selections: ScenarioTacticSelection[] = []

  meceTable.pillars.forEach((pillar, pillarIdx) => {
    pillar.tactics.forEach((_, tacticIdx) => {
      selections.push({
        pillar_index: pillarIdx,
        tactic_index: tacticIdx,
        inclusion: 'maybe'
      })
    })
  })

  return {
    id: `scenario-${Date.now()}`,
    name,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    tactic_selections: selections,
    is_baseline: false
  }
}

// Helper to clone a scenario
function cloneScenario(source: WhatIfScenario, newName: string): WhatIfScenario {
  return {
    id: `scenario-${Date.now()}`,
    name: newName,
    description: `Based on ${source.name}`,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    tactic_selections: source.tactic_selections.map(s => ({ ...s })),
    is_baseline: false
  }
}

const scenariosSlice = createSlice({
  name: 'scenarios',
  initialState,
  reducers: {
    // Initialize scenarios for a conversation with baseline from MECE table
    initializeScenarios: (
      state,
      action: PayloadAction<{ conversationId: string; meceTable: MECETable }>
    ) => {
      const { conversationId, meceTable } = action.payload

      // Only initialize if not already done
      if (!state.byConversation[conversationId]) {
        const baseline = createBaselineFromMece(meceTable)
        state.byConversation[conversationId] = {
          scenarios: [baseline],
          active_scenario_id: baseline.id,
          comparison_mode: false,
          comparison_scenario_ids: null,
          show_impact_panel: false
        }
      }
    },

    // Create a new scenario
    createScenario: (
      state,
      action: PayloadAction<{
        conversationId: string
        name: string
        fromBaseline: boolean
        meceTable: MECETable
      }>
    ) => {
      const { conversationId, name, fromBaseline, meceTable } = action.payload
      const convState = state.byConversation[conversationId]

      if (!convState) return

      let newScenario: WhatIfScenario

      if (fromBaseline) {
        // Clone from the baseline scenario
        const baseline = convState.scenarios.find(s => s.is_baseline)
        if (baseline) {
          newScenario = cloneScenario(baseline, name)
        } else {
          newScenario = createFreshScenario(meceTable, name)
        }
      } else {
        // Create fresh scenario with all 'maybe'
        newScenario = createFreshScenario(meceTable, name)
      }

      convState.scenarios.push(newScenario)
      convState.active_scenario_id = newScenario.id
    },

    // Delete a scenario (cannot delete baseline)
    deleteScenario: (
      state,
      action: PayloadAction<{ conversationId: string; scenarioId: string }>
    ) => {
      const { conversationId, scenarioId } = action.payload
      const convState = state.byConversation[conversationId]

      if (!convState) return

      // Find the scenario
      const scenario = convState.scenarios.find(s => s.id === scenarioId)
      if (!scenario || scenario.is_baseline) return // Can't delete baseline

      // Remove the scenario
      convState.scenarios = convState.scenarios.filter(s => s.id !== scenarioId)

      // If the deleted scenario was active, switch to baseline
      if (convState.active_scenario_id === scenarioId) {
        const baseline = convState.scenarios.find(s => s.is_baseline)
        convState.active_scenario_id = baseline?.id || null
      }

      // Clear comparison if deleted scenario was being compared
      if (convState.comparison_scenario_ids?.includes(scenarioId)) {
        convState.comparison_mode = false
        convState.comparison_scenario_ids = null
      }
    },

    // Rename a scenario
    renameScenario: (
      state,
      action: PayloadAction<{ conversationId: string; scenarioId: string; newName: string }>
    ) => {
      const { conversationId, scenarioId, newName } = action.payload
      const convState = state.byConversation[conversationId]

      if (!convState) return

      const scenario = convState.scenarios.find(s => s.id === scenarioId)
      if (scenario && !scenario.is_baseline) {
        scenario.name = newName
        scenario.updated_at = new Date().toISOString()
      }
    },

    // Select active scenario
    selectScenario: (
      state,
      action: PayloadAction<{ conversationId: string; scenarioId: string }>
    ) => {
      const { conversationId, scenarioId } = action.payload
      const convState = state.byConversation[conversationId]

      if (!convState) return

      if (convState.scenarios.some(s => s.id === scenarioId)) {
        convState.active_scenario_id = scenarioId
      }
    },

    // Update tactic inclusion state
    updateTacticInclusion: (
      state,
      action: PayloadAction<{
        conversationId: string
        scenarioId: string
        pillarIdx: number
        tacticIdx: number
        inclusion: TacticInclusionState
      }>
    ) => {
      const { conversationId, scenarioId, pillarIdx, tacticIdx, inclusion } = action.payload
      const convState = state.byConversation[conversationId]

      if (!convState) return

      const scenario = convState.scenarios.find(s => s.id === scenarioId)
      if (!scenario) return

      // Find the tactic selection
      const selection = scenario.tactic_selections.find(
        s => s.pillar_index === pillarIdx && s.tactic_index === tacticIdx
      )

      if (selection) {
        selection.inclusion = inclusion
        scenario.updated_at = new Date().toISOString()
      }
    },

    // Toggle comparison mode
    toggleComparisonMode: (
      state,
      action: PayloadAction<{ conversationId: string }>
    ) => {
      const { conversationId } = action.payload
      const convState = state.byConversation[conversationId]

      if (!convState) return

      convState.comparison_mode = !convState.comparison_mode

      // If turning off, clear comparison scenarios
      if (!convState.comparison_mode) {
        convState.comparison_scenario_ids = null
      }
    },

    // Set scenarios for comparison
    setComparisonScenarios: (
      state,
      action: PayloadAction<{ conversationId: string; scenarioIds: [string, string] }>
    ) => {
      const { conversationId, scenarioIds } = action.payload
      const convState = state.byConversation[conversationId]

      if (!convState) return

      convState.comparison_scenario_ids = scenarioIds
      convState.comparison_mode = true
    },

    // Toggle impact panel visibility
    toggleImpactPanel: (
      state,
      action: PayloadAction<{ conversationId: string }>
    ) => {
      const { conversationId } = action.payload
      const convState = state.byConversation[conversationId]

      if (!convState) return

      convState.show_impact_panel = !convState.show_impact_panel
    },

    // Clear scenario state for a conversation
    clearScenarios: (
      state,
      action: PayloadAction<{ conversationId: string }>
    ) => {
      const { conversationId } = action.payload
      delete state.byConversation[conversationId]
    },

    // Exit scenario mode for a conversation
    exitScenarioMode: (
      state,
      action: PayloadAction<{ conversationId: string }>
    ) => {
      const { conversationId } = action.payload
      // Keep the scenarios but reset view state
      const convState = state.byConversation[conversationId]
      if (convState) {
        convState.comparison_mode = false
        convState.comparison_scenario_ids = null
        convState.show_impact_panel = false
      }
    }
  }
})

// Actions
export const {
  initializeScenarios,
  createScenario,
  deleteScenario,
  renameScenario,
  selectScenario,
  updateTacticInclusion,
  toggleComparisonMode,
  setComparisonScenarios,
  toggleImpactPanel,
  clearScenarios,
  exitScenarioMode
} = scenariosSlice.actions

// Selectors
export const selectScenarioState = (state: RootState, conversationId: string) =>
  state.scenarios?.byConversation[conversationId] || null

export const selectActiveScenario = (state: RootState, conversationId: string) => {
  const convState = state.scenarios?.byConversation[conversationId]
  if (!convState) return null
  return convState.scenarios.find(s => s.id === convState.active_scenario_id) || null
}

export const selectAllScenarios = (state: RootState, conversationId: string) =>
  state.scenarios?.byConversation[conversationId]?.scenarios || []

export const selectIsScenarioMode = (state: RootState, conversationId: string) =>
  !!state.scenarios?.byConversation[conversationId]

export const selectComparisonMode = (state: RootState, conversationId: string) =>
  state.scenarios?.byConversation[conversationId]?.comparison_mode || false

export const selectShowImpactPanel = (state: RootState, conversationId: string) =>
  state.scenarios?.byConversation[conversationId]?.show_impact_panel || false

export const selectComparisonScenarios = (state: RootState, conversationId: string) => {
  const convState = state.scenarios?.byConversation[conversationId]
  if (!convState || !convState.comparison_scenario_ids) return null

  const [idA, idB] = convState.comparison_scenario_ids
  const scenarioA = convState.scenarios.find(s => s.id === idA)
  const scenarioB = convState.scenarios.find(s => s.id === idB)

  if (!scenarioA || !scenarioB) return null
  return [scenarioA, scenarioB] as [WhatIfScenario, WhatIfScenario]
}

export default scenariosSlice.reducer
